import { Anchor } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import { SUPPORT_EMAIL } from "@/constants/support";

import { CptCodesHelpTextContext } from "../types/procedure-codes";

export interface CptCodesHelpTextProps {
  context: CptCodesHelpTextContext;
}

/**
 * Explains that the listed CPT® codes are a starter set and how to bill any other code. The
 * wording follows the surface it appears on: the Settings fee schedule points to the Custom tab,
 * while the procedures picker points to the practice admin, since providers and scribes cannot
 * open Settings. Support is linked in both variants for codes the practice uses often.
 *
 * @param props.context - Surface the text is rendered on, `settings` or `picker`.
 * @returns A help text block containing a mailto link to SUPPORT_EMAIL.
 */
export default function CptCodesHelpText({ context }: CptCodesHelpTextProps) {
  const supportLink = (
    <Anchor fz="sm" c="burgundy.9" href={`mailto:${SUPPORT_EMAIL}`}>
      {SUPPORT_EMAIL}
    </Anchor>
  );

  return (
    <TextWrapper size="sm" c="gray.7" data-testid="cpt-codes-help-text">
      {context === "settings" ? (
        <>
          These are common CPT® codes. To bill any other code, add it under Custom. For codes you
          use often, contact {supportLink}.
        </>
      ) : (
        <>
          Search for any CPT code. If you can&apos;t find it, ask your practice admin to add it as a
          custom code, or contact {supportLink} for codes you use often.
        </>
      )}
    </TextWrapper>
  );
}
